import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { SalesTrendChart } from '../../dashboard/SalesTrendChart';
import { OrdersTrendChart } from '../../dashboard/OrdersTrendChart';
import { SalesUnitsChart } from '../../dashboard/SalesUnitsChart';
import { MarginTrendChart } from '../../dashboard/MarginTrendChart';
import { DevolucionesTrendChart } from '../../dashboard/DevolucionesTrendChart';
import { TopProductsChart } from '../../dashboard/TopProductsChart';
import { TopProductsComparisonChart } from '../../dashboard/TopProductsComparisonChart';
import { StockIngressTrendChart } from '../../dashboard/StockIngressTrendChart';
import { StockAvailableByLocationChart } from '../../dashboard/StockAvailableByLocationChart';
import { InventoryStatusChart } from '../../dashboard/InventoryStatusChart';
import { InventoryTransfersChart } from '../../dashboard/InventoryTransfersChart';
import { InventoryRecordsChart } from '../../dashboard/InventoryRecordsChart';
import { OrdersStatusChart } from '../../dashboard/OrdersStatusChart';
import { ComparisonTrendChart } from '../../dashboard/ComparisonTrendChart';
import { ProductTrendChart } from '../../dashboard/ProductTrendChart';
import type {
  ChartType,
  MonthComparisonPoint,
  YearComparisonPoint,
} from '../../../types/statistics.types';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { ComparisonMeta } from './Statistics.hooks';
import {
  CHARTS_WITH_COMPARISON,
  CHARTS_WITH_PARTIAL_COMPARISON,
} from '../../../types/statistics.types';
import type { DashboardUnitsPointDto } from '../../../api/models/DashboardUnitsPointDto';
import type { DashboardMarginPointDto } from '../../../api/models/DashboardMarginPointDto';
import type { DashboardDevolucionesPointDto } from '../../../api/models/DashboardDevolucionesPointDto';
import type { DashboardStockIngressPointDto } from '../../../api/models/DashboardStockIngressPointDto';
import type { DashboardStockByLocationPointDto } from '../../../api/models/DashboardStockByLocationPointDto';

type DashboardMode = 'light' | 'dark';

export interface StatisticsChartProps {
  chartType: ChartType;
  chartData: unknown;
  loading: boolean;
  mode?: DashboardMode;
  isGlobalScope: boolean;
  /** Presente cuando chartData es resultado de comparación de períodos */
  comparisonMeta?: import('./Statistics.hooks').ComparisonMeta;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CHART_TYPES_WITHOUT_TREND = ['porCobrar', 'intercambios', 'ticket'] as const;

const CHART_TITLES: Record<string, string> = {
  sales: 'Ventas en dinero',
  orders: 'Número de ventas',
  units: 'Piezas vendidas',
  margin: 'Margen',
  devoluciones: 'Devoluciones',
  'stock-ingress': 'Stock ingresado',
};

const CHART_VALUE_FORMAT: Record<string, 'currency' | 'number'> = {
  sales: 'currency',
  orders: 'number',
  units: 'number',
  margin: 'currency',
  devoluciones: 'number',
  'stock-ingress': 'number',
};

/** Garantiza que los gráficos que esperan arrays reciban siempre un array (evita chartData.map is not a function al cambiar de tipo) */
const safeArray = <T,>(data: unknown): T[] => (Array.isArray(data) ? (data as T[]) : []);

const StatisticsChart: React.FC<StatisticsChartProps> = ({
  chartType,
  chartData,
  loading,
  mode = 'light',
  isGlobalScope,
  comparisonMeta,
}) => {
  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 2,
            minHeight: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: mode === 'light' ? '#bae6fd' : 'rgba(255,255,255,0.1)',
          }}
        >
          <Typography color="text.secondary">Cargando gráfica...</Typography>
        </Paper>
      </Box>
    );
  }

  const commonProps = { mode, loading: false };

  const useComparisonChart =
    comparisonMeta &&
    (CHARTS_WITH_COMPARISON.includes(chartType) ||
      CHARTS_WITH_PARTIAL_COMPARISON.includes(chartType));
  const comparisonData = safeArray<MonthComparisonPoint | YearComparisonPoint>(chartData);

  const renderComparisonOrRegular = (
    regularChart: React.ReactNode,
    chartId: keyof typeof CHART_TITLES,
  ) => {
    if (useComparisonChart && comparisonMeta) {
      return (
        <Box sx={{ mt: 4 }}>
          <ComparisonTrendChart
            {...commonProps}
            data={comparisonData}
            comparisonMeta={comparisonMeta}
            valueFormat={CHART_VALUE_FORMAT[chartId] ?? 'number'}
            title={CHART_TITLES[chartId] ?? chartId}
          />
        </Box>
      );
    }
    return <Box sx={{ mt: 4 }}>{regularChart}</Box>;
  };

  switch (chartType) {
    case 'sales':
      return renderComparisonOrRegular(
        <SalesTrendChart
          {...commonProps}
          data={safeArray<{ date: string; orders: number; total: number }>(chartData)}
        />,
        'sales',
      );
    case 'orders':
      return renderComparisonOrRegular(
        <OrdersTrendChart
          {...commonProps}
          data={safeArray<{ date: string; orders: number; total: number }>(chartData)}
        />,
        'orders',
      );
    case 'units':
      return renderComparisonOrRegular(
        <SalesUnitsChart {...commonProps} data={safeArray<DashboardUnitsPointDto>(chartData)} />,
        'units',
      );
    case 'margin':
      return renderComparisonOrRegular(
        <MarginTrendChart {...commonProps} data={safeArray<DashboardMarginPointDto>(chartData)} />,
        'margin',
      );
    case 'devoluciones':
      return renderComparisonOrRegular(
        <DevolucionesTrendChart
          {...commonProps}
          data={safeArray<DashboardDevolucionesPointDto>(chartData)}
        />,
        'devoluciones',
      );
    case 'top-products':
      if (comparisonMeta?.isPartial) {
        return (
          <Box sx={{ mt: 4 }}>
            <TopProductsComparisonChart
              {...commonProps}
              data={safeArray<Record<string, unknown>>(chartData)}
              comparisonMeta={comparisonMeta}
            />
          </Box>
        );
      }
      return (
        <Box sx={{ mt: 4 }}>
          <TopProductsChart
            {...commonProps}
            data={safeArray<{
              productId: number;
              productName: string;
              count: number;
              revenue: number;
            }>(chartData)}
          />
        </Box>
      );
    case 'stock-ingress':
      return renderComparisonOrRegular(
        <StockIngressTrendChart
          {...commonProps}
          data={safeArray<DashboardStockIngressPointDto>(chartData)}
        />,
        'stock-ingress',
      );
    case 'stock-by-location':
      if (!isGlobalScope) {
        return (
          <Box sx={{ mt: 4 }}>
            <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography color="text.secondary">
                Stock por sucursal solo está disponible en vista Global.
              </Typography>
            </Paper>
          </Box>
        );
      }
      return (
        <Box sx={{ mt: 4 }}>
          <StockAvailableByLocationChart
            {...commonProps}
            data={safeArray<DashboardStockByLocationPointDto>(chartData)}
          />
        </Box>
      );
    case 'inventory-status':
      return (
        <Box sx={{ mt: 4 }}>
          <InventoryStatusChart
            {...commonProps}
            data={safeArray<{ estado: string; cantidad: number }>(chartData)}
          />
        </Box>
      );
    case 'inventory-transfers':
      return (
        <Box sx={{ mt: 4 }}>
          <InventoryTransfersChart
            {...commonProps}
            data={safeArray<{ estado: string; cantidad: number }>(chartData)}
          />
        </Box>
      );
    case 'inventory-records':
      return (
        <Box sx={{ mt: 4 }}>
          <InventoryRecordsChart
            {...commonProps}
            data={safeArray<{ estado: string; cantidad: number }>(chartData)}
          />
        </Box>
      );
    case 'orders-status':
      return (
        <Box sx={{ mt: 4 }}>
          <OrdersStatusChart
            {...commonProps}
            data={safeArray<{ estado: string; cantidad: number }>(chartData)}
          />
        </Box>
      );
    case 'product-trend': {
      const productTrendData = chartData as {
        productKeys?: string[];
        productLabels?: string[];
        data?: Array<{ date: string; unitsByProduct: Record<string, number> }>;
      } | null;
      const hasProductTrendData =
        productTrendData &&
        Array.isArray(productTrendData.productKeys) &&
        productTrendData.productKeys.length > 0 &&
        Array.isArray(productTrendData.data);
      if (hasProductTrendData) {
        return (
          <Box sx={{ mt: 4 }}>
            <ProductTrendChart
              {...commonProps}
              data={{
                productKeys: productTrendData.productKeys ?? [],
                productLabels: productTrendData.productLabels ?? [],
                data: productTrendData.data ?? [],
              }}
            />
          </Box>
        );
      }
      return (
        <Box sx={{ mt: 4 }}>
          <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography color="text.secondary">
              Seleccione entre 1 y 4 productos en el selector de arriba para ver la tendencia de
              ventas.
            </Typography>
          </Paper>
        </Box>
      );
    }
    default:
      return (
        <Box sx={{ mt: 4 }}>
          <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography color="text.secondary">Seleccione un tipo de gráfica.</Typography>
          </Paper>
        </Box>
      );
  }
};

export default StatisticsChart;
